<template>
  <div class="sidebar">
    <div class="sidebar__inner">
      <div class="sidebar__logo">
        <img
          :src="`/img/renova-dashboard/logo.svg`"
          alt="logo"
          class="sidebar__logo-img cursor__pointer"
          @click="reloadPage"
        />
        <div class="sidebar__mobile-menu">
          <BaseSimpleDropdown
            v-if="!languageSelector"
            :position="'bottom-left'"
            :offsetTop="38"
            :min-width="'220px'"
            :options="[
              {
                text: $t('adminTitles.interfaceLanguage'),
                id: 'language',
              },
              {
                text: $t('adminTitles.StartGuide'),
                id: 'helpCenter',
              },
                {
                text: $t('adminTitles.logout'),
                id: 'logout',
              },
            ]"
            @selectOption="menuOptionClick($event)"
          >
            <template #optionButton>
              <img
                src="/img/renova-dashboard/icons/burger-menu.svg"
                alt="edit"
              />
            </template>
          </BaseSimpleDropdown>
          <LangSwitcher ref="LanguageSelector" v-if="languageSelector"
                        @toggleDropdown="!$event ? languageSelector = false : null">
            <img
              src="/img/renova-dashboard/icons/burger-menu.svg"
              alt="edit"
            />
          </LangSwitcher>
        </div>
      </div>
      <div class="sidebar__dropdown">
        <BaseSimpleDropdown
          :options="dropdownOptions"
          :selected-option="selectedPageOption"
          :button-text="$t('adminTitles.Pages')"
          :position="'bottom-left'"
          :width="'100%'"
          :min-width="'100%'"
          :offset-top="56"
          @selectOption="selectPageOption"
        >
          <template #optionButton>
            <div class="sidebar__dropdown-button">
              <img
                :src="selectedDropdownOption ? selectedDropdownOption.icon : '/img/renova-dashboard/menu/dashboard.svg'"
                alt="pages"
                class="sidebar__link-icon"
              />
              <span>{{
                  selectedDropdownOption ? selectedDropdownOption.text : $t('adminTitles.Pages')
                }}</span>
              <i class="el-icon-arrow-down color--dark-purple"></i>
            </div>
          </template>
        </BaseSimpleDropdown>
      </div>
      <div class="sidebar__links">
        <template v-for="(item, itemIdx) in topRoutesWithSections">
          <div
            v-if="item.isSectionHeader"
            :key="`section-${item.sectionKey}`"
            class="sidebar__section-header"
          >
            {{ $t(`adminTitles.${item.sectionKey}`) }}
          </div>
          <SideBarLink v-else :key="itemIdx" :link="item"></SideBarLink>
        </template>
        <div class="sidebar__links-bottom">
          <div class="sidebar__separator"></div>
          <SideBarLink
            v-if="isUserAdmin && showStartOnboarding"
            :link="startOnboardingLink"
            class="sidebar__link--bottom-zone"
            @linkClick="startOnboarding()"
          ></SideBarLink>
          <section v-for="(link, itemIdx) in bottomRoutes" :key="itemIdx">
            <SideBarLink :link="link" class="sidebar__link--bottom-zone"></SideBarLink>
          </section>
          <SideBarLink
            :link="startGuideLink"
            class="sidebar__link--bottom-zone"
            @linkClick="helpCenterOpen = true"
          ></SideBarLink>
        </div>
      </div>
    </div>
    <HelpCenterDialog v-if="helpCenterOpen" @close="helpCenterOpen = false"/>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {AUTH_LOGOUT} from '@/store/actions/auth';
import SideBarLink from '@/views/RenovaDashboard/CommonParts/SideBar/SideBarLink';
import licenseMixin from '@/mixins/licenseMixin';
import {CLEAR_ONBOARDING} from '@/store/actions/onboarding';
import {checkOptionalUserPermissions, PermissionsTypes} from '@/utils/permissions';
import BaseSimpleDropdown
  from '@/views/RenovaDashboard/components/ui-kit/Dropdowns/BaseSimpleDropdown';
import LangSwitcher from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/LangSwitcher';
import HelpCenterDialog from '@/views/RenovaDashboard/CommonParts/SideBar/HelpCenterDialog';
import {USER_ROLES} from '@/store/modules/user';

export default {
  name: 'TheSideBar',
  components: {
    LangSwitcher,
    BaseSimpleDropdown,
    SideBarLink,
    HelpCenterDialog
  },
  mixins: [licenseMixin],
  computed: {
    PermissionsTypes() {
      return PermissionsTypes;
    },
    isUserAdmin() {
      return this.userRole === USER_ROLES.ADMIN;
    },
    showStartOnboarding() {
      return window.isDev
        || window.location.hostname === 'localhost'
        || this.selectedInsuranceProviderDetails?.name?.toLowerCase()?.includes('demo');
    },
    startOnboardingLink() {
      return {
        name: this.isOnboardingSimulated ? 'StopOnboarding' : 'StartOnboarding',
        customEvent: true,
        renovaDashboardIcon: '/img/renova-dashboard/menu/onboarding.svg',
        renovaDashboardActiveIcon: '/img/renova-dashboard/menu/onboarding--active.svg',
      };
    },
    ...mapGetters(['user', 'userRole', 'ticketList', 'selectedInsuranceProviderDetails', 'permissions', 'providerType', 'isOnboardingSimulated']),
    salesHubRoutes() {
      // Routes that should be shown for the "salesHub" provider type
      return ['dashboard', 'Tasks', 'EmployeesSettings', 'AddOperators', 'Integrations', 'ClaraAI', 'adminHelp', 'Leads', 'providerType', 'InsuredList', 'tickets'];
    },
    // Routes available under any hub — gated by their permission only, NOT by the
    // sales-hub allowlist. The backend already returns the permission only when the
    // hub allows it (e.g. PLANS_* is in anyHubPermissions), so a hard hub-lock here
    // would be redundant. Coverage/Programs (REN-1107) is such a route.
    anyHubRoutes() {
      return ['programs'];
    },
    // When seats are exceeded the admin can only reach Add Team (to remove
    // operators) and their profile/billing link (rendered separately via
    // providerLink). Everything else gets a padlock.
    seatsExceededAllowed() {
      return ['AddOperators'];
    },
    allTopRoutes() {
      // Build full list without filtering by permissions or provider type; we'll attach locker messages instead
      const routes = this.$router.options.routes
        .filter((item) => (item.meta && item.meta.menu && !item.meta.bottomLink))
        .filter((item) => (item.meta.excludedRoles ? !item.meta.excludedRoles.includes(this.userRole) : true));

      return routes.map((item) => {
        const meta = {...item.meta};
        // Respect existing featureLockerText if already provided in route meta
        if (!meta.featureLockerText) {
          const notInSalesHub = this.providerType === 'sales_hub'
            && !this.anyHubRoutes.includes(meta.name)
            && !this.salesHubRoutes.includes(meta.name);
          const lacksPermission = meta.permissions ? !checkOptionalUserPermissions(meta.permissions, this.permissions) : false;
          const blockedBySeats = this.seatsExceeded && !this.seatsExceededAllowed.includes(meta.name);

          if (notInSalesHub) {
            meta.featureLockerText = 'adminTitles.ServiceHubIncluded';
          } else if (blockedBySeats) {
            meta.featureLockerText = 'subscription.seatsExceededLock';
          } else if (lacksPermission) {
            meta.featureLockerText = 'adminTitles.FeatureNotAvailableForRole';
          }
        }
        return meta;
      });
    },
    dropdownRoutes() {
      const mobileRoutes = ['tickets', 'Leads'];
      return this.allTopRoutes.filter((route) => mobileRoutes.includes(route.name));
    },
    dropdownOptions() {
      return this.dropdownRoutes.map((route) => ({
        id: route.name,
        text: route.text || this.$t(`adminTitles.${route.name}`),
        icon: route.renovaDashboardIconBlack,
        path: route.path
      }));
    },
    topRoutes() {
      return [...this.allTopRoutes].sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999));
    },
    topRoutesWithSections() {
      const allRoutes = [...this.topRoutes, { ...this.providerLink, menuOrder: 8 }]
        .sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999));
      let lastSection = null;
      return allRoutes.reduce((result, link) => {
        if (link.section && link.section !== lastSection) {
          result.push({ isSectionHeader: true, sectionKey: link.section });
          lastSection = link.section;
        }
        result.push(link);
        return result;
      }, []);
    },
    bottomRoutes() {
      const routes = this.$router.options.routes
        .filter((item) => (item.meta && item.meta.menu && item.meta.bottomLink));

      return routes
        .map((item) => {
          const meta = { ...item.meta };
          if (!meta.featureLockerText) {
            const notInSalesHub = this.providerType === 'sales_hub'
              && !this.anyHubRoutes.includes(meta.name)
              && !this.salesHubRoutes.includes(meta.name);
            const lacksPermission = meta.permissions ? !checkOptionalUserPermissions(meta.permissions, this.permissions) : false;
            const blockedBySeats = this.seatsExceeded && !this.seatsExceededAllowed.includes(meta.name);

            if (notInSalesHub) {
              meta.featureLockerText = 'adminTitles.ServiceHubIncluded';
            } else if (blockedBySeats) {
              meta.featureLockerText = 'subscription.seatsExceededLock';
            } else if (lacksPermission) {
              meta.featureLockerText = 'adminTitles.FeatureNotAvailableForRole';
            }
          }
          return meta;
        })
        .sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999));
    },
    providerLink() {
      const meta = {
        text: `${this.$t('adminTitles.MyProfile')}`,
        path: `/admin/provider/${this.selectedInsuranceProviderDetails?.id}`,
        renovaDashboardIcon: '/img/renova-dashboard/menu/provider.png',
        renovaDashboardActiveIcon: '/img/renova-dashboard/menu/provider--active.svg',
        badgeKey: 'companyProfile',
      };
      // Lock if user lacks permission to view profile
      const lacksPermission = !checkOptionalUserPermissions([PermissionsTypes.MY_PROFILE_VIEW], this.permissions);
      if (lacksPermission) {
        meta.featureLockerText = 'adminTitles.FeatureNotAvailableForRole';
      }
      return meta;
    },
    selectedDropdownOption() {
      if (!this.selectedPageOption) return null;
      return this.dropdownOptions.find((option) => option.id === this.selectedPageOption);
    },
  },
  data() {
    return {
      languageSelector: false,
      selectedPageOption: null,
      helpCenterOpen: false,
      startGuideLink: {
        name: 'StartGuide',
        customEvent: true,
        renovaDashboardIcon: '/img/renova-dashboard/menu/Book-open.svg',
      },
    };
  },
  created() {
    // Set initial selected option based on current route
    this.setInitialSelectedOption();
  },
  watch: {
    $route: {
      handler() {
        this.setInitialSelectedOption();
      },
      immediate: true,
    },
  },
  methods: {
    checkOptionalUserPermissions,
    logout() {
      this.$store.dispatch(AUTH_LOGOUT)
        .then(() => {
          this.$router.push('/login');
        });
    },
    reloadPage() {
      window.location.reload();
    },
    startOnboarding() {
      if (this.isOnboardingSimulated) {
        this.$store.commit('SET_ONBOARDING_SIMULATE', false);
        return;
      }
      this.$store.commit('CLEAR_FIRST_HUB');
      this.$store.commit('SET_ONBOARDING_SIMULATE', true);
      this.$store.dispatch(CLEAR_ONBOARDING);
    },
    menuOptionClick(option) {
      switch (option) {
        case 'logout':
          return this.logout();
        case 'helpCenter':
          this.helpCenterOpen = true;
          break;
        case 'language':
          this.languageSelector = true;
          setTimeout(() => {
            this.$refs.LanguageSelector.$refs.LanguageSelector.toggleDropdown();
          });
          break;
        default:
          break;
      }
    },
    setInitialSelectedOption() {
      const currentPath = this.$route.path;

      const currentRoute = this.dropdownRoutes.find((route) => currentPath.startsWith(route.path));

      if (currentRoute) {
        this.selectedPageOption = currentRoute.name;
      }
    },
    selectPageOption(optionId) {
      this.selectedPageOption = optionId;
      const selectedRoute = this.dropdownRoutes.find((route) => route.name === optionId);
      if (selectedRoute) {
        this.$router.push(selectedRoute.path);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.sidebar {
  min-width: 240px;
  z-index: 100;
  min-height: 100%;
  padding: 16px;
  background: var(--solid-black-purple);

  &__logo {
    .sidebar__logo-img {
      width: 177px;
    }

    margin-bottom: 16px;
  }

  &__links {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 114px);
    overflow: auto;
  }
}

// The default scrollbar reads badly here: this is a near-black panel, and the browser draws a
// bar meant for a light one. The two platforms need different tools, and they are mutually
// exclusive — setting `scrollbar-color` makes Chrome ignore `::-webkit-scrollbar` entirely.
//
// Where the native bar is a fading overlay (macOS) it has to be replaced outright, or there is
// nothing to see. `.has-overlay-scrollbars` is set by `@/utils/scrollbars`.
.has-overlay-scrollbars .sidebar__links {
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(217, 210, 255, 0.28);
    border-radius: 5px;

    &:hover {
      background: rgba(217, 210, 255, 0.45);
    }
  }
}

// Everywhere else the native bar is already visible and carries the OS stepper arrows, so it
// only wants toning down. `scrollbar-color` recolours it in place rather than replacing it,
// which is the whole point — the arrows stay.
html:not(.has-overlay-scrollbars) .sidebar__links {
  scrollbar-color: rgba(217, 210, 255, 0.32) transparent;
}

.sidebar__section-header {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: rgba(217, 210, 255, 0.4);
  padding: 12px 8px 4px;
  text-transform: uppercase;
}

.sidebar__separator {
  height: 1px;
  background: rgba(217, 210, 255, 0.15);
  margin: 4px 0;
}

::v-deep .sidebar__link--bottom-zone {
  .sidebar__link {
    p {
      color: rgba(217, 210, 255, 0.55);
    }

    img {
      opacity: 0.55;
    }

    &.router-link-active {
      p {
        color: white;
        font-size: 12px;
      }

      img {
        opacity: 1;
      }
    }

    &:hover {
      p {
        color: rgba(217, 210, 255, 0.85);
      }

      img {
        opacity: 0.85;
      }
    }
  }
}

.sidebar__mobile-menu {
  display: none;
}

.sidebar__dropdown {
  display: none;
  margin-top: 24px;
}

.sidebar__dropdown-button {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  background: #FFFFFF;
  padding: 12px;
  border-radius: 8px;
}

.sidebar__dropdown-button span {
  font-size: 14px;
  line-height: 16px;
  color: var(--solid-black-purple);
  margin-left: 8px;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.sidebar__dropdown-button .sidebar__link-icon {
  width: 24px;
  height: 24px;
}

.sidebar__dropdown .simple-dropdown__options {
  background-color: var(--solid-black-purple);
  border: 1px solid var(--solid-purple);
  width: 100%;
}

.sidebar__dropdown .simple-dropdown__option {
  background-color: transparent;
  color: #D9D2FF;
}

.sidebar__dropdown .simple-dropdown__option:hover {
  background-color: var(--solid-purple);
}

.sidebar__dropdown .simple-dropdown__option--active {
  color: white !important;
  background-color: var(--solid-purple);
}

@media screen and (max-width: 668px) {
  .sidebar {
    width: 100vw;
    min-height: 80px;
    padding: 20px;
  }
  .sidebar__logo {
    margin: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;

    .sidebar__logo-img {
      width: 147px;
      height: 40px;
    }
  }
  .sidebar__links {
    display: none;
  }
  .sidebar__mobile-menu {
    display: flex;
  }
  .sidebar__dropdown {
    display: block;
  }
}
</style>
